import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { App } from '../../../models/app';
import { useTranslation } from 'react-i18next';
import { useAppInstallRequiresAnotherPlan } from '../../../hooks/useAppInstallRequiresAnotherPlan';
import { Paper, Typography } from '@material-ui/core';
import { useFetchMerchantPlansQuery } from '../../../redux/api/appMarket-api-slice';
import { generateRoute } from '../../../utils/location';
import { AppMarketPageIdentifier } from '../../../AppRoutes';
import { getUsableMerchantPlans } from '../../../component-helpers/merchant-plans';
import styles from './styles.module.scss';

interface ComponentProps {
  app: App;
  setAnotherPlanError: () => void;
}

export const RequiresAnotherPlan: React.FC<ComponentProps> = ({ app, setAnotherPlanError }): Nullable<JSX.Element> => {
  const { t } = useTranslation();
  const history = useHistory();
  const { requiresAnotherPlan, unSupportedPlan } = useAppInstallRequiresAnotherPlan(app);
  const { data: merchantPlans, isLoading } = useFetchMerchantPlansQuery(undefined, {
    skip: !requiresAnotherPlan,
  });
  const { name: appName } = app;

  const usablePlans = useMemo(() => {
    if (!merchantPlans) return [];
    return getUsableMerchantPlans(app, merchantPlans, unSupportedPlan?.id);
  }, [app, merchantPlans, unSupportedPlan?.id]);

  const showError = requiresAnotherPlan && !isLoading && !usablePlans.length;

  useEffect(() => {
    if (showError) setAnotherPlanError();
  }, [showError, setAnotherPlanError]);

  /* This Query Parameter is being passed to the billing route so that the merchant can navigate back to App Details Page */
  const navigateBackToAppDetailsPage = `?back=${generateRoute(AppMarketPageIdentifier.AppDetails, app.id)}`;

  const gotoPlan = (planId: Uuid) => {
    history.push(generateRoute(AppMarketPageIdentifier.MerchantPlans, [planId,navigateBackToAppDetailsPage]));
  };

  if (!usablePlans.length) return null;

  return (
    <Paper square>
      <h3>{t('merchant.plans.requires_another_plan')}</h3>
      <Typography variant='body1' color={'textSecondary'}>
        {t('merchant.plans.requires_plan_long', { appName })}
      </Typography>
      <div className={styles.anotherPlans}>
        {
          usablePlans.map((plan) => {
            return (
              <div
                key={plan.id}
                role='button'
                tabIndex={0}
                className={styles.anotherPlan}
                onClick={() => gotoPlan(plan.id)}
                onKeyPress={() => gotoPlan(plan.id)}
              >
                <Typography variant='subtitle1' color='textPrimary' className={styles.title} gutterBottom>
                  {t(plan.title)}
                </Typography>
                <Typography variant='body1'>
                  {t(plan.planTranslationKey, {appName})}
                </Typography>
              </div>
            );
          })
        }
      </div>
    </Paper>
  );
};
